import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { SkillSuggestionData } from '@edtech-ui/core/models/resume-builder/soft-skills-user-resume';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-resumer-builder-skills',
	standalone: true,
	imports: [MatIconModule, ReactiveFormsModule, FormsModule, CommonModule],
	templateUrl: './resumer-builder-skills.component.html',
	styleUrl: './resumer-builder-skills.component.scss'
})
export class ResumerBuilderSkillsComponent implements OnInit, OnDestroy {


	topicInput: string = '';
	skillInput: string = '';
	technologyInput: string = '';
	isLoadingSkills = false;
	selectedTopics: string[] = [];
	selectedTechnologies: string[] = [];
	selectedSoftSkills: string[] = [];
	subscription = new Subscription();
	targetRole: string = '';
	softSkills: string[] = [];
	technicalSkills: string[] = [];
	technology: string[] = [];


	constructor(
		private resumeService: ResumeService,
		private _toastr: ToastrService,
		private route: ActivatedRoute
	) { }

	ngOnInit(): void {
		const resumeId = this.route.snapshot.paramMap.get('resumeId');
		if (resumeId) {
			this.resumeService.getFullResumeData().subscribe(data => {

				if (data) {
					this.targetRole = data.targetRole;

					if (data?.skills) {
						this.editSkillsData(data.skills);
					}

					if (this.targetRole) {
						this.getSoftSkills();
					}
				}
			});
		} else {
			this.targetRole = this.resumeService.targetRole.getValue()
			if (this.targetRole) {
				this.getSoftSkills();
			}
			const data = this.resumeService.getSectionData('skills');

			if (data) {
				this.selectedTopics = data.technicalSkills || [];
				this.selectedTechnologies = data.tools || [];
				this.selectedSoftSkills = data.softSkills || [];
			}
		}
	}


	suggestedTopics: string[] = [
		'JavaScript', 'TypeScript', 'Angular', 'React', 'Node.js',
		'Python', 'Java', 'C#', 'SQL', 'HTML/CSS'
	];


	suggestedTechnologies: string[] = [
		'Microsoft Office', 'Google WorkSpace', 'GitHub', 'Canva', 'Zoom', 'Slack', 'Trello', 'ChatGPT'
	]


	suggestedSoftSkills: string[] = [
		'Communication', 'Teamwork', 'Problem-solving', 'Time management', 'Adaptability',
		'Critical thinking', 'Leadership', 'Creativity', 'Work ethic', 'Emotional intelligence'
	]


	addTopic() {
		const topic = this.topicInput.trim();
		if (topic && !this.selectedTopics.includes(topic)) {
			this.selectedTopics.push(topic);
		}
		this.topicInput = '';
	}


	toggleTopic(topic: string) {
		const index = this.selectedTopics.indexOf(topic);

		if (index > -1) {
			this.selectedTopics.splice(index, 1);
		} else {
			this.selectedTopics.push(topic);
		}
	}


	filteredTopics(): string[] {
		const input = this.topicInput.toLowerCase();
		const selected = this.suggestedTopics.filter(t => this.selectedTopics.includes(t));
		const notSelected = this.suggestedTopics.filter(t =>
			!this.selectedTopics.includes(t) && t.toLowerCase().includes(input)
		);
		return [...selected, ...notSelected];
	}


	addTechnologyTopic() {
		const topic = this.technologyInput.trim();
		if (topic && !this.selectedTechnologies.includes(topic)) {
			this.selectedTechnologies.push(topic);
		}
		this.technologyInput = '';
	}


	addSoftSkillTopic() {
		const topic = this.skillInput.trim();
		if (topic && !this.selectedSoftSkills.includes(topic)) {
			this.selectedSoftSkills.push(topic);
		}
		this.skillInput = '';
	}


	toggleTechnologyTopic(topic: string) {
		const index = this.selectedTechnologies.indexOf(topic);
		if (index > -1) {
			this.selectedTechnologies.splice(index, 1);
		} else {
			this.selectedTechnologies.push(topic);
		}
	}


	toggleSoftSkillTopic(topic: string) {
		const index = this.selectedSoftSkills.indexOf(topic);
		if (index > -1) {
			this.selectedSoftSkills.splice(index, 1);
		} else {
			this.selectedSoftSkills.push(topic);
		}
	}



	filteredTechnologiesTopics(): string[] {
		const input = this.technologyInput.toLowerCase();

		const selected = this.suggestedTechnologies.filter(t =>
			this.selectedTechnologies.includes(t));

		const notSelected = this.suggestedTechnologies.filter(t =>
			!this.selectedTechnologies.includes(t) &&
			t.toLowerCase().includes(input));

		return [...selected, ...notSelected];
	}

	
	filteredSoftSkillsTopics(): string[] {
		const input = this.skillInput.toLowerCase();
		const selected = this.suggestedSoftSkills.filter(t => this.selectedSoftSkills.includes(t));
		const notSelected = this.suggestedSoftSkills.filter(t =>
			!this.selectedSoftSkills.includes(t) && t.toLowerCase().includes(input)
		);
		return [...selected, ...notSelected];
	}


	getSoftSkills() {
		if (!this.targetRole) return;

		this.isLoadingSkills = true;

		this.subscription.add(
			this.resumeService.getStudentSoftSkills(this.targetRole).pipe(

				tap(response => {
					if (response?.Success && response?.Data) {

						const data = response.Data;

						this.softSkills = data.softSkills || [];
						this.technicalSkills = data.technicalSkills || [];
						this.technology = data.tools || [];

						if (this.technicalSkills.length > 0) {
							this.suggestedTopics = this.technicalSkills;
						}

						if (this.technology.length > 0) {
							this.suggestedTechnologies = this.technology;
						}

						if (this.softSkills.length > 0) {
							this.suggestedSoftSkills = this.softSkills;
						}

					}
				}),
				finalize(() => {
					this.isLoadingSkills = false;
				}),
				catchError(() => {
					this._toastr.error('Failed to load skills. Showing default suggestions.');
					return of(null);
				}),

				finalize(() => {
					this.isLoadingSkills = false;
				})

			).subscribe()
		);
	}


	saveData() {
		const payload = {
			technicalSkills: this.selectedTopics,
			tools: this.selectedTechnologies,
			softSkills: this.selectedSoftSkills
		};
		this.resumeService.setSectionData('skills', payload);
	}

	editSkillsData(data: any) {
		if (!data) return;
		this.selectedTopics = data.technicalSkills || [];
		this.selectedTechnologies = data.tools || [];
		this.selectedSoftSkills = data.softSkills || [];
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
